import React, { useState } from 'react';
import xss from 'xss';
import { Link } from 'gatsby';
import SearchHero from './Hero';
import SearchBar, { OnSearchRes } from './SearchBar';
import SearchFilterDesktop, { SearchFilterMobile } from './SearchFilter';
import { DrupalNodeType, SearchResult } from '../../search';
import ExternalLinkIcon from '../Icons/ExternalLinkIcon';

import './Search.scss';
import Pagination from '../Generics/Pagination';
import { translateLanguage } from '../utils/UtilityFunctions';

interface CardsProps {
  cards: SearchResult[];
}

interface SearchComponentProps {
  currentLanguage: string;
  currentPath: string;
}

interface SearchResultsMessageProps {
  searchValue: string;
  count: number;
  language: string;
}

export function displayMessage(count: number, searchValue: string, language: string) {
  if (count > 1 && searchValue) {
    return translateLanguage(
      language,
      `${count} results found for "${searchValue}"`,
      `${count} resultados encontrados para "${searchValue}"`
    );
  } else if (count === 1 && searchValue) {
    return translateLanguage(
      language,
      `${count} result found for "${searchValue}"`,
      `${count} resultado encontrado para "${searchValue}"`
    );
  } else if (count === 0 && searchValue) {
    return translateLanguage(
      language,
      `No search results found for "${searchValue}"`,
      `No se encontraron resultados de búsqueda para "${searchValue}"`
    );
  } else {
    return '';
  }
}

export function createCardContent(card: SearchResult) {
  switch (card.__typename) {
    case DrupalNodeType.Service:
      return (
        <div className="search-filter-card__agency-container">
          <a
            className="search-filter-card__title"
            href={card.fieldServiceLink.url.path}
            target="__blank"
          >
            {card.entityLabel}
          </a>

          <a
            href={card.fieldServiceLink.url.path}
            aria-label={`External website for ${card.entityLabel}`}
            target="__blank"
          >
            <ExternalLinkIcon className="search-filter-card__external-icon" />
          </a>
        </div>
      );

    case DrupalNodeType.County:
      return (
        <div className="search-filter-card__agency-container">
          <a
            className="search-filter-card__title"
            href={card.fieldCountyLink.url.path}
            target="__blank"
          >
            {card.entityLabel}
          </a>

          <a
            href={card.fieldCountyLink.url.path}
            aria-label={`External website for ${card.entityLabel}`}
            target="__blank"
          >
            <ExternalLinkIcon className="search-filter-card__external-icon" />
          </a>
        </div>
      );

    case DrupalNodeType.Agency:
      return (
        <div className="search-filter-card__agency-container">
          <a className="search-filter-card__title" href={card.fieldAgencyLink.uri} target="__blank">
            {card.entityLabel}
          </a>

          <a
            href={card.fieldAgencyLink.uri}
            aria-label={`External website for ${card.entityLabel}`}
            target="__blank"
          >
            <ExternalLinkIcon className="search-filter-card__external-icon" />
          </a>
        </div>
      );

    case DrupalNodeType.ChildPage:
    case DrupalNodeType.MainLandingPage:
    case DrupalNodeType.TexansOfTexas:
      return (
        <>
          <Link className="search-filter-card__title" to={card.entityUrl.path}>
            {card.entityLabel}
          </Link>

          <div
            className="search-filter-card__description"
            dangerouslySetInnerHTML={{ __html: xss(card.fieldDescription.value) }}
          />
        </>
      );
  }
}

export function SearchResultsMessage({ count, searchValue, language }: SearchResultsMessageProps) {
  return (
    <p className="search-filter-results__message">{displayMessage(count, searchValue, language)}</p>
  );
}

export function SearchCards({ cards }: CardsProps) {
  return (
    <>
      {cards.map((card) => (
        <div key={card.__typename + card.entityId} className="search-filter-card">
          {createCardContent(card)}
        </div>
      ))}
    </>
  );
}

//primary point of state management for search results display
export default function SearchComponent({ currentLanguage, currentPath }: SearchComponentProps) {
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const limit = 10;

  const getData = (res: OnSearchRes) => {
    setResults(res.res);
    setSearchValue(res.searchTerm);
    setCurrentPage(res.currentPage);
    setTotalResults(res.totalResults);
  };

  const handleSelectedFilter = (filter: string) => {
    setSelectedFilter(filter);
  };

  return (
    <>
      <SearchHero language={currentLanguage} />
      <div className="wrapper wrapper--grey-7">
        <div className="container">
          <aside className="desktop-filter-view">
            <SearchFilterDesktop
              selectedFilter={selectedFilter}
              onSelectedFilter={handleSelectedFilter}
              language={currentLanguage}
            />
          </aside>

          <main className="w-2/3">
            <section>
              <SearchBar
                onSearch={getData}
                language={currentLanguage}
                limit={limit}
                typeFilter={selectedFilter}
              />
            </section>

            <section className="mobile-filter-view">
              {isOpen && (
                <SearchFilterMobile
                  selectedFilter={selectedFilter}
                  onSelectedFilter={handleSelectedFilter}
                  language={currentLanguage}
                />
              )}

              <button
                className="search-filter__mobile-filter-button"
                onClick={() => {
                  isOpen ? setIsOpen(false) : setIsOpen(true);
                }}
                aria-expanded={isOpen}
              >
                {isOpen
                  ? translateLanguage(currentLanguage, 'Close', 'Cerrar')
                  : translateLanguage(currentLanguage, 'Open filters', 'Abrir')}
              </button>
            </section>

            <section>
              <SearchResultsMessage
                searchValue={searchValue}
                count={totalResults}
                language={currentLanguage}
              />

              <SearchCards cards={results} />
            </section>

            {(results.length > 1 || currentPage > 1) && (
              <section className="search__pagination-wrapper">
                <Pagination
                  currentLanguage={currentLanguage}
                  totalItems={totalResults}
                  pageSize={limit}
                  currentPage={currentPage}
                  createLinkUrl={(n) =>
                    n === 1
                      ? currentPath + `?search=${searchValue}`
                      : `${currentPath}/?search=${searchValue}&page=${n}`
                  }
                />
              </section>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
